import type { RequisitionAction } from '@nest-recruit/shared';

// S10 — where a hiring manager lands (§L22 note, ADR 0012).
//
// Every notification keeps one declared deep link (S8), because an HR Lead and a
// hiring manager can receive the same N-04. A manager's world is the three HM
// screens (E16), so any REQ link they open — from an email, the bell, or a bookmark —
// is turned into the matching HM screen before anything renders. `beforeLoad` does
// it, so it is a route change inside the app, never a page load (§5a rule 7).

export type HmLanding =
  | { readonly to: '/my/actions'; readonly search: { readonly step?: string; readonly request?: string } }
  | { readonly to: '/my/openings'; readonly search: { readonly open?: string } }
  | { readonly to: '/request-hire' };

/** What a requisition's page would offer this manager, when that decides the landing. */
export type NeedsAction = (requisitionId: string) => Promise<boolean | null>;

const DETAIL = /^\/requisitions\/([^/]+)$/;
const EDIT = /^\/requisitions\/([^/]+)\/edit$/;

/**
 * The HM screen for an S9 path, or null to leave the path alone.
 *
 * `needsAction` answers for a plain REQ-03 link, whose status decides where it goes:
 * a draft or a rejection the manager owns is waiting on them (HM-02), anything else
 * is news about an opening (HM-01). Null from it means the requisition could not be
 * read — the path is left alone so REQ-03 shows its own 403 or 404 (S10 QA test 3).
 */
export async function hmLandingFor(
  pathname: string,
  search: Readonly<Record<string, unknown>>,
  needsAction: NeedsAction,
): Promise<HmLanding | null> {
  const path = pathname.length > 1 ? pathname.replace(/\/$/, '') : pathname;

  if (path === '/' || path === '/approvals') return { to: '/my/actions', search: {} };
  if (path === '/requisitions') return { to: '/my/openings', search: {} };
  if (path === '/requisitions/new') return { to: '/request-hire' };

  const edit = EDIT.exec(path);
  if (edit?.[1]) return { to: '/my/actions', search: { request: edit[1] } };

  const detail = DETAIL.exec(path);
  if (!detail?.[1]) return null;
  const requisitionId = detail[1];

  // N-01 / N-02: the approval itself.
  if (typeof search.decide === 'string' && search.decide !== '') {
    return { to: '/my/actions', search: { step: search.decide } };
  }
  const isWaiting = await needsAction(requisitionId);
  if (isWaiting === null) return null;
  return isWaiting
    ? { to: '/my/actions', search: { request: requisitionId } }
    : { to: '/my/openings', search: { open: requisitionId } };
}

/** The owner's next move on a draft or a rejection is theirs — HM-02 lists it. */
export function isWaitingOnOwner(actions: readonly RequisitionAction[]): boolean {
  return actions.includes('submit') || actions.includes('revise');
}
